/*!
 * GentsHub Journal — gh-journal.js
 * Vanilla JS, no external libraries, no jQuery.
 * Handles: reading progress, table of contents, article sharing,
 *          journal hub filter + sort + load more.
 * Progressive enhancement: all content is usable without JS.
 */

(function () {
  'use strict';

  /* ── UTILS ──────────────────────────────────────────────────── */

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.from((ctx || document).querySelectorAll(sel)); }

  function isRTL() {
    return document.documentElement.getAttribute('dir') === 'rtl';
  }

  /* ── READING PROGRESS BAR ───────────────────────────────────── */

  function initProgressBar() {
    var bar = document.getElementById('gh-reading-progress');
    if (!bar) return;

    var content = document.getElementById('gh-article-content');
    if (!content) return;

    function update() {
      var rect = content.getBoundingClientRect();
      var total = content.offsetHeight;
      var scrolled = -rect.top;
      var pct = total > 0 ? Math.max(0, Math.min(100, (scrolled / total) * 100)) : 0;
      bar.style.width = pct + '%';
      bar.setAttribute('aria-valuenow', Math.round(pct));
    }

    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  /* ── TABLE OF CONTENTS ──────────────────────────────────────── */

  function initToC() {
    var content  = document.getElementById('gh-article-content');
    var deskToc  = document.getElementById('gh-toc-desktop');
    var mobList  = document.getElementById('gh-toc-mobile-list');
    var mobWrap  = document.getElementById('gh-toc-mobile');
    var layout   = document.getElementById('gh-article-layout');

    if (!content) return;

    var headings = $$('h2, h3', content);

    if (headings.length < 3) {
      if (deskToc)  deskToc.hidden  = true;
      if (mobWrap)  mobWrap.hidden  = true;
      if (layout)   layout.classList.add('gh-article__layout--no-toc');
      return;
    }

    /* Assign unique IDs */
    var seen = {};
    headings.forEach(function (h) {
      if (!h.id) {
        var base = (h.textContent || '')
          .trim()
          .toLowerCase()
          .replace(/[^\w\u0600-\u06FF\s-]/g, '')
          .replace(/\s+/g, '-')
          .replace(/-+/g, '-')
          .substring(0, 60);
        if (!base) base = 'section';
        var id = base;
        var n  = 1;
        while (seen[id]) { id = base + '-' + (++n); }
        seen[id] = true;
        h.id = id;
      } else {
        seen[h.id] = true;
      }
    });

    /* Build a <ul> of TOC links and inject into a container */
    function buildList(container) {
      if (!container) return;
      var ul = document.createElement('ul');
      ul.className = 'gh-toc__list';
      ul.setAttribute('role', 'list');

      headings.forEach(function (h) {
        var li = document.createElement('li');
        li.className = 'gh-toc__item' + (h.tagName === 'H3' ? ' gh-toc__item--h3' : '');

        var a = document.createElement('a');
        a.href      = '#' + h.id;
        a.className = 'gh-toc__link';
        a.textContent = (h.textContent || '').trim();

        a.addEventListener('click', function (e) {
          e.preventDefault();
          var target = document.getElementById(h.id);
          if (!target) return;
          var hh  = parseInt(
            getComputedStyle(document.documentElement).getPropertyValue('--header-height') || '80',
            10
          );
          var top = target.getBoundingClientRect().top + window.pageYOffset - hh - 24;
          window.scrollTo({ top: top, behavior: 'smooth' });

          /* Close mobile details on click */
          if (mobWrap && mobWrap.open) mobWrap.removeAttribute('open');
        });

        li.appendChild(a);
        ul.appendChild(li);
      });

      container.appendChild(ul);
    }

    buildList(deskToc);
    buildList(mobList);

    /* Active link via IntersectionObserver */
    if (!('IntersectionObserver' in window)) return;

    var tocLinks = $$('.gh-toc__link');

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            tocLinks.forEach(function (a) {
              var active = a.getAttribute('href') === '#' + entry.target.id;
              a.classList.toggle('gh-toc__link--active', active);
            });
          }
        });
      },
      { rootMargin: '-5% 0px -80% 0px' }
    );

    headings.forEach(function (h) { observer.observe(h); });
  }

  /* ── ARTICLE SHARING — copy link ───────────────────────────── */

  function initSharing() {
    $$('.gh-copy-link-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var url  = window.location.href;
        var wrap = btn.closest('.gh-article__share');
        var msg  = wrap ? wrap.querySelector('.gh-copy-confirm') : null;

        function showMsg() {
          if (!msg) return;
          msg.classList.add('gh-copy-confirm--visible');
          setTimeout(function () { msg.classList.remove('gh-copy-confirm--visible'); }, 2500);
        }

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).then(showMsg).catch(function () { fallback(url, showMsg); });
        } else {
          fallback(url, showMsg);
        }
      });
    });

    function fallback(text, cb) {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.style.cssText = 'position:fixed;opacity:0;top:0;left:0;';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); cb(); } catch (e) { /* silent */ }
      document.body.removeChild(ta);
    }
  }

  /* ── JOURNAL HUB — filter + sort by date + load more ────────── */

  function initJournalHub() {
    var hub = document.getElementById('gh-journal-hub');
    if (!hub) return;

    var grid        = document.getElementById('gh-journal-grid');
    if (!grid) return;

    var filterBtns  = $$('.gh-filter-btn', hub);
    var loadMoreBtn = document.getElementById('gh-load-more-btn');
    var noResults   = document.getElementById('gh-no-results');
    var featuredUrl = (hub.dataset.featuredUrl || '').trim();

    var INITIAL     = parseInt(hub.dataset.initialCount  || '12', 10);
    var BATCH       = parseInt(hub.dataset.loadMoreCount || '6',  10);
    var currentFilter = 'all';

    /* ① Remove duplicate URLs (same blog selected twice in blocks) */
    var seenUrls = Object.create(null);
    $$('.gh-acard', grid).forEach(function (c) {
      var u = c.dataset.url || '';
      if (seenUrls[u]) { c.parentNode && c.parentNode.removeChild(c); }
      else { seenUrls[u] = true; }
    });

    /* ② Sort cards newest-first by data-published (Unix timestamp) */
    var allCards = $$('.gh-acard', grid);

    allCards.sort(function (a, b) {
      return (parseInt(b.dataset.published || '0', 10)) -
             (parseInt(a.dataset.published || '0', 10));
    });

    allCards.forEach(function (c) { grid.appendChild(c); });

    /* ③ Re-collect after sort */
    allCards = $$('.gh-acard', grid);

    /* ④ Apply current filter + load-more window */
    function applyState() {
      var visIdx = 0;

      allCards.forEach(function (card) {
        var isFeatured  = featuredUrl && card.dataset.url === featuredUrl;
        var isFiltered  = currentFilter !== 'all' && card.dataset.blogHandle !== currentFilter;

        if (isFeatured || isFiltered) {
          card.setAttribute('data-filtered', 'true');
          card.removeAttribute('data-hidden');
          return;
        }

        card.removeAttribute('data-filtered');
        visIdx++;

        if (visIdx <= INITIAL) {
          card.removeAttribute('data-hidden');
        } else {
          card.setAttribute('data-hidden', 'true');
        }
      });

      var stillHidden = allCards.filter(function (c) {
        return c.getAttribute('data-hidden') === 'true' &&
               c.getAttribute('data-filtered') !== 'true';
      });

      if (loadMoreBtn) {
        if (stillHidden.length > 0) { loadMoreBtn.removeAttribute('hidden'); }
        else { loadMoreBtn.setAttribute('hidden', ''); }
      }

      /* No-results message */
      var anyVisible = allCards.some(function (c) {
        return c.getAttribute('data-filtered') !== 'true' &&
               c.getAttribute('data-hidden')   !== 'true';
      });

      if (noResults) { noResults.hidden = anyVisible; }
    }

    /* ⑤ Populate filter counts */
    function populateCounts() {
      filterBtns.forEach(function (btn) {
        var handle  = btn.dataset.filter;
        var countEl = btn.querySelector('.gh-filter-count');
        if (!countEl) return;

        var cnt;
        if (handle === 'all') {
          cnt = allCards.filter(function (c) {
            return !(featuredUrl && c.dataset.url === featuredUrl);
          }).length;
        } else {
          cnt = allCards.filter(function (c) {
            return c.dataset.blogHandle === handle &&
                   !(featuredUrl && c.dataset.url === featuredUrl);
          }).length;
        }

        countEl.textContent = '(' + cnt + ')';
      });
    }

    /* ⑥ Filter button interaction */
    filterBtns.forEach(function (btn) {
      btn.addEventListener('click', activate);

      btn.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          activate.call(btn);
        }
      });

      function activate() {
        currentFilter = btn.dataset.filter;
        INITIAL = parseInt(hub.dataset.initialCount || '12', 10); /* reset window */

        filterBtns.forEach(function (b) {
          b.setAttribute('aria-pressed', 'false');
          b.classList.remove('gh-filter-btn--active');
        });

        btn.setAttribute('aria-pressed', 'true');
        btn.classList.add('gh-filter-btn--active');

        applyState();
      }
    });

    /* ⑦ Load More */
    if (loadMoreBtn) {
      loadMoreBtn.addEventListener('click', function () {
        var hidden = $$('.gh-acard[data-hidden="true"]', grid)
                       .filter(function (c) { return c.getAttribute('data-filtered') !== 'true'; });

        hidden.slice(0, BATCH).forEach(function (c) { c.removeAttribute('data-hidden'); });

        var stillHidden = $$('.gh-acard[data-hidden="true"]', grid)
                            .filter(function (c) { return c.getAttribute('data-filtered') !== 'true'; });

        if (stillHidden.length === 0) { loadMoreBtn.setAttribute('hidden', ''); }
      });
    }

    /* ⑧ Init */
    populateCounts();
    applyState();
  }

  /* ── BOOTSTRAP ───────────────────────────────────────────────── */

  function init() {
    initProgressBar();
    initToC();
    initSharing();
    initJournalHub();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
